<form [formGroup]="contentLibraryRuleForm" (ngSubmit)="onSave(true)">
    <r1-compass-lib-instructions headerText="Edit Rule">
        Use the editor below to create the content that should be written to the Talking Points field if the record
        matches the below criteria. You can personalize this content with DataMart or File Date (Play Importer plays
        only) fields by dragging the field merges into the editor.
    </r1-compass-lib-instructions>
    <div class="rule-container">
        <div class="content-name-row">
            <mat-form-field class="rule-name-field">
                <mat-label>Rule Name</mat-label>
                <input matInput type="text" [formControl]="contentLibraryRuleForm.controls.name" />
            </mat-form-field>
        </div>
        <div class="field-comparator-row">
            @if (type() === 'datamart') {
                <mat-form-field class="fields-dropdown">
                    <mat-label>Select a Field</mat-label>
                    <mat-select
                        [formControl]="contentLibraryRuleForm.controls.fieldName"
                        (selectionChange)="onFieldSelectionChange()">
                        @for (field of fields(); track $index) {
                            <mat-option [value]="field.name">{{ field.displayName }}</mat-option>
                        }
                    </mat-select>
                    @if (contentLibraryRuleForm.controls.fieldName.errors?.['required']) {
                        <mat-error>Field required</mat-error>
                    }
                </mat-form-field>
            } @else if (type() === 'csv') {
                <div class="csv-field">
                    <mat-form-field class="csv-field-dropdown">
                        <mat-label>Select a Field</mat-label>
                        <mat-select
                            [formControl]="contentLibraryRuleForm.controls.fieldName"
                            (selectionChange)="onFieldSelectionChange()">
                            @for (mapping of validityMappings(); track $index) {
                                @for (field of mapping | keyvalue; track $index) {
                                    <mat-option
                                        [value]="field.key"
                                        (click)="contentLibraryRuleForm.controls.dataType.reset()"
                                        >{{ field.key }}
                                    </mat-option>
                                }
                            }
                        </mat-select>
                        @if (contentLibraryRuleForm.controls.fieldName.errors?.['required']) {
                            <mat-error>Field required</mat-error>
                        }
                    </mat-form-field>
                    @for (mapping of validityMappings(); track $index) {
                        @for (field of mapping | keyvalue; track $index) {
                            @if (field.key === contentLibraryRuleForm.controls.fieldName.value) {
                                <mat-form-field class="csv-field-dropdown">
                                    <mat-label>Field Type</mat-label>
                                    <mat-select
                                        [formControl]="contentLibraryRuleForm.controls.dataType"
                                        (selectionChange)="
                                            contentLibraryRuleForm.patchValue({ comparator: '', value: '' })
                                        ">
                                        @for (type of field.value | keyvalue; track $index) {
                                            <mat-option [value]="type.key">{{ type.key }}</mat-option>
                                        }
                                    </mat-select>
                                    @if (contentLibraryRuleForm.controls.dataType.errors?.['required']) {
                                        <mat-error>Field required</mat-error>
                                    }
                                </mat-form-field>
                            }
                        }
                    }
                </div>
            } @else {
                <div class="loading-filler"></div>
            }
            <div class="checkbox-container">
                @if (showNotCheckbox()) {
                    <mat-checkbox [formControl]="contentLibraryRuleForm.controls.not">Not</mat-checkbox>
                }
            </div>
            <mat-form-field class="comparator-dropdown">
                <mat-label>Comparator</mat-label>
                <mat-select
                    [formControl]="contentLibraryRuleForm.controls.comparator"
                    (selectionChange)="onComparatorSelectionChange($event)">
                    @for (comparator of comparators(); track $index) {
                        <mat-option [value]="comparator.value">{{ comparator.label }}</mat-option>
                    }
                </mat-select>
                @if (contentLibraryRuleForm.controls.comparator.errors?.['required']) {
                    <mat-error>Field required</mat-error>
                }
            </mat-form-field>
        </div>
        @if (showValueRow()) {
            <div class="value-row">
                @switch (selectedComparatorValueType()) {
                    @case ('text') {
                        <mat-form-field class="single-value-form-field">
                            <mat-label>Value</mat-label>
                            <input matInput [formControl]="contentLibraryRuleForm.controls.value" />
                            @if (contentLibraryRuleForm.controls.value.hasError('required')) {
                                <mat-error>Value required</mat-error>
                            }
                        </mat-form-field>
                    }
                    @case ('textarea') {
                        <mat-form-field class="single-value-form-field">
                            <mat-label>Value</mat-label>
                            <textarea matInput [formControl]="contentLibraryRuleForm.controls.value"></textarea>
                            @if (contentLibraryRuleForm.controls.value.hasError('required')) {
                                <mat-error>Value required</mat-error>
                            }
                        </mat-form-field>
                    }
                    @case ('typeahead') {
                        <div class="single-value-form-field">
                            <r1-compass-lib-typeahead-search
                                [valueFormControl]="contentLibraryRuleForm.controls.value"
                                [displayFormControl]="contentLibraryRuleForm.controls.value"
                                [resultDisplayPropertyPath]="'display'"
                                [resultValuePropertyPath]="'value'"
                                [disabled]="false"
                                [searchResults]="(valueTypeaheadResults$ | async) ?? []"
                                (resultSelection)="valueTypeaheadSearch.set('')"
                                (search)="valueTypeaheadSearch.set($event)" />
                        </div>
                    }
                    @case ('picklist') {
                        <mat-form-field class="single-value-form-field">
                            <mat-label>Value</mat-label>
                            <mat-select [formControl]="contentLibraryRuleForm.controls.value">
                                @for (option of picklistOptions(); track option) {
                                    <mat-option [value]="option.value">{{ option.displayName }}</mat-option>
                                }
                            </mat-select>
                            @if (contentLibraryRuleForm.controls.value.hasError('required')) {
                                <mat-error>Value required</mat-error>
                            }
                        </mat-form-field>
                    }
                    @case ('multipick') {
                        <r1-compass-lib-autocomplete-chip-buffet
                            [allEntities]="picklistOptions()"
                            [multiPickEntitiesFormCtrl]="multiPickFormControlEntities()"
                            (multiPickEntitiesFormCtrlChange)="multiPickChange($event)" />
                    }
                    @case ('date') {
                        <mat-form-field class="single-value-form-field">
                            <mat-label>Select a date</mat-label>
                            <input
                                matInput
                                readonly
                                [matDatepicker]="datePicker"
                                [formControl]="contentLibraryRuleForm.controls.value"
                                (click)="datePicker.open()"
                                (dateInput)="
                                    contentLibraryRuleForm.patchValue({
                                        value: $event.value?.toISOString().replace('.000', ''),
                                    })
                                " />
                            <fa-icon matSuffix class="picker-icon" [icon]="faCalendar" (click)="datePicker.open()" />
                            <mat-datepicker #datePicker />
                            @if (contentLibraryRuleForm.controls.value.hasError('required')) {
                                <mat-error>Value required</mat-error>
                            }
                        </mat-form-field>
                    }
                    @case ('nothing') {
                        <div [hidden]="true">
                            <mat-form-field>
                                <input matInput />
                            </mat-form-field>
                        </div>
                    }
                    @default {
                        <div [hidden]="true">
                            <mat-form-field>
                                <input matInput />
                            </mat-form-field>
                        </div>
                    }
                }
            </div>
        }
    </div>
    <div class="content-editor-container">
        <r1-compass-app-content-editor
            [editorContentType]="'content'"
            [assetId]="assetId()!"
            [froalaContentFormControl]="contentLibraryRuleForm.controls.talkingPointsTemplate"
            [fileImporterHeaders]="play()?.config?.playFileImport?.headers ?? []"
            [showErrors]="true" />
    </div>
    <div class="component-footer">
        <button class="button button-brilliant-blue" [disabled]="disableSave()" type="submit">Save</button>
        <button class="button button-white" (click)="onCancel()" type="button">Cancel</button>
    </div>
</form>
